import isNil from 'lodash/isNil';
import { useEffect } from 'react';

import {
  couponCookie,
  usePromotion,
  isContextPrimary,
  isShadowing,
  ReferrerType,
} from '@farmersdog/coupons';
import type { PromotionContextTreatment } from '@farmersdog/coupons';

import { FeatureName } from '../../abTesting/features';
import { useContentFeature } from '../../abTesting/useContentFeature';

import { experimentCodesWhitelist } from './constants';
import { setCouponCookieWithDefaultExperimentCode } from './utils/setCouponCookieWithDefaultExperimentCode';

export function useDefaultDiscountExperiment(): void {
  const { isReady, treatment, config } = useContentFeature(
    FeatureName.CVR_DEFAULT_DISCOUNT,
    {}
  );

  const { treatment: rawDiscountContextTreatment } = useContentFeature(
    FeatureName.MT_DISCOUNT_CONTEXT,
    {}
  );
  const discountContextTreatment =
    rawDiscountContextTreatment as PromotionContextTreatment;

  const {
    couponCode,
    loading: promotionLoading,
    setPromotion,
  } = usePromotion();

  const isExperimentOn = isReady && treatment !== 'off';
  const shouldUseContext = isContextPrimary(discountContextTreatment);
  const shouldShadow = isShadowing(discountContextTreatment);

  useEffect(() => {
    if (!isExperimentOn) {
      return;
    }

    // defer until context has loaded
    if ((shouldUseContext || shouldShadow) && promotionLoading) {
      return;
    }

    const currentCode = shouldUseContext
      ? couponCode
      : couponCookie.get().referrerCode;

    /*
      If user is in the experiment and ANY NON-DEFAULT coupon cookie value
      exists, we will not overwrite it.
      If user is in the experiment and a DEFAULT coupon cookie value exists,
      we will overwrite it with the latest code returned from split.
    */
    const isDefault = currentCode && experimentCodesWhitelist.has(currentCode);

    if (!isNil(currentCode) && !isDefault) {
      return;
    }

    const splitConfig: Record<string, unknown> = config ?? {};

    if (shouldUseContext || shouldShadow) {
      const code = splitConfig.code as string | undefined;
      if (code) {
        void setPromotion({
          code,
          referrerType: ReferrerType.Partner,
          skipCookieWrites: !shouldUseContext,
        });
      }
    }

    if (!shouldUseContext || shouldShadow) {
      setCouponCookieWithDefaultExperimentCode({ splitConfig });
    }
  }, [
    isExperimentOn,
    config,
    shouldUseContext,
    shouldShadow,
    couponCode,
    promotionLoading,
    setPromotion,
  ]);
}
